/**
 * Conciliação Cielo por DATA (ledger).
 *
 * Substitui a conferência por lote: o arquivo é só insumo. A importação faz
 * upsert por (data, bandeira, tipo) — reimportar nunca empilha estado — e
 * responde com um DIFF ("o que mudou"), com as datas sem ação colapsadas.
 * As decisões pertencem à data: uma divergência antiga continua visível e
 * decidível no calendário, sem reimportar nada.
 *
 * O banner de cobertura diz até onde a movimentação está carregada e de onde
 * gerar o próximo arquivo "progressivo"; o calendário mostra o estado de cada
 * dia (aplicada / pendente / divergente) e o clique abre o detalhe com as
 * ações — a mesma trilha de dinheiro da conferência (baixa parcelas e lança ou
 * reusa o crédito, com dedup).
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Upload, AlertTriangle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight,
  ChevronUp, Landmark, Loader2, History, CalendarDays, Copy,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardContent } from '@components/Card';
import { financialReportsService } from '@services/financialReportsService';
import { formatCurrencyBRL, formatDateBR } from './financialUtils';
import { staleDetailMessage } from './cieloLedgerUtils';
import { formatCnpj } from '@/types/company';
import type {
  SettlementDayDetail,
  SettlementDayGroupView,
  SettlementDayStatus,
  SettlementDaySummary,
  SettlementImportDayDiff,
  SettlementImportDiff,
} from '@/types/cardReceivable';

interface CieloLedgerPanelProps {
  /** Empresa e conta vêm da tela de Conta Corrente — é lá que o usuário já as escolheu. */
  companyId: number;
  accountId: number;
  companyName: string;
  accountLabel: string;
}

const formatCurrency = (cents: number): string => formatCurrencyBRL(cents / 100);
const formatDate = (iso: string): string => formatDateBR(iso) || '—';

const pad2 = (n: number): string => String(n).padStart(2, '0');
const isoOf = (year: number, month: number, day: number): string => `${year}-${pad2(month)}-${pad2(day)}`;

const MONTH_LABELS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const WEEKDAY_LABELS = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'];

const STATUS_META: Record<SettlementDayStatus, { label: string; badge: string; cell: string }> = {
  aplicada: {
    label: 'Conferida',
    badge: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    cell: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-200',
  },
  pendente: {
    label: 'Pendente',
    badge: 'bg-amber-50 border-amber-200 text-amber-700',
    cell: 'bg-amber-100 text-amber-800 hover:bg-amber-200',
  },
  divergente: {
    label: 'Divergente',
    badge: 'bg-red-50 border-red-200 text-red-700',
    cell: 'bg-red-100 text-red-800 hover:bg-red-200',
  },
  agendada: {
    label: 'Agendada',
    badge: 'bg-sky-50 border-sky-200 text-sky-700',
    cell: 'bg-sky-100 text-sky-800 hover:bg-sky-200',
  },
};

const OUTCOME_META: Record<string, { label: string; className: string }> = {
  nova: { label: 'Nova', className: 'bg-sky-50 border-sky-200 text-sky-700' },
  sem_mudanca: { label: 'Sem mudança', className: 'bg-slate-50 border-slate-200 text-slate-600' },
  registros_novos: { label: 'Registros novos', className: 'bg-amber-50 border-amber-200 text-amber-700' },
  ja_aplicada: { label: 'Já aplicada', className: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
  agendada: { label: 'Agendada', className: 'bg-sky-50 border-sky-200 text-sky-700' },
};

const CATEGORY_LABEL: Record<string, string> = {
  confere: 'Confere',
  so_no_arquivo: 'Só no arquivo',
  so_na_tabela: 'Só na previsão',
  divergente: 'Divergente',
  ja_importado: 'Já importado',
};

const MATCH_SOURCE_LABEL: Record<string, string> = {
  tid: 'por TID',
  nsu: 'por NSU',
  pedido: 'pela referência do pedido',
  nao_identificado: 'não identificada',
};

export const CieloLedgerPanel: React.FC<CieloLedgerPanelProps> = ({
  companyId,
  accountId,
  companyName,
  accountLabel,
}) => {
  const today = new Date();
  const [cursor, setCursor] = useState({ year: today.getFullYear(), month: today.getMonth() + 1 });

  const [coverage, setCoverage] = useState<Awaited<ReturnType<typeof financialReportsService.getCieloCoverage>> | null>(null);
  const [loadingCoverage, setLoadingCoverage] = useState(false);

  const [diff, setDiff] = useState<SettlementImportDiff | null>(null);
  const [fileName, setFileName] = useState('');
  const [importing, setImporting] = useState(false);
  const [expandedDiff, setExpandedDiff] = useState<Set<string>>(new Set());

  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dayDetail, setDayDetail] = useState<SettlementDayDetail | null>(null);
  const [loadingDay, setLoadingDay] = useState(false);

  // Transações do Detalhado expandidas por grupo (chave = contexto + grupo).
  const [expandedEntries, setExpandedEntries] = useState<Set<string>>(new Set());

  // Confirmação explícita antes de tocar o caixa — quem baixa relê a conta e o
  // valor antes de confirmar.
  const [pendingDecision, setPendingDecision] = useState<{
    rowId: number; decision: 'baixar' | 'nao_recebido'; label: string; amountCents: number;
  } | null>(null);
  const [deciding, setDeciding] = useState(false);

  const [copied, setCopied] = useState(false);
  const copyEstablishment = async (ec: string) => {
    try {
      await navigator.clipboard.writeText(ec);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Sem permissão de clipboard: o número continua visível para copiar à mão.
    }
  };

  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // O painel do dia (onde estão as ações) fica no fim da página — sem rolar
  // até ele, o clique em "decidir no dia" parece não fazer nada.
  const dayDetailRef = useRef<HTMLDivElement>(null);

  const monthFrom = isoOf(cursor.year, cursor.month, 1);
  const monthTo = isoOf(cursor.year, cursor.month, new Date(cursor.year, cursor.month, 0).getDate());

  const loadCoverage = useCallback(async () => {
    try {
      setLoadingCoverage(true);
      const data = await financialReportsService.getCieloCoverage(companyId, monthFrom, monthTo);
      setCoverage(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar a cobertura');
    } finally {
      setLoadingCoverage(false);
    }
  }, [companyId, monthFrom, monthTo]);

  useEffect(() => { void loadCoverage(); }, [loadCoverage]);

  const daysByDate = useMemo(() => {
    const map = new Map<string, SettlementDaySummary>();
    for (const day of coverage?.days ?? []) map.set(day.date, day);
    return map;
  }, [coverage]);

  const openDay = useCallback(async (date: string) => {
    setSelectedDate(date);
    setPendingDecision(null);
    // Rola até o painel do dia assim que ele existir no DOM.
    setTimeout(() => dayDetailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
    try {
      setLoadingDay(true);
      setDayDetail(await financialReportsService.getCieloDay(companyId, date));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar o dia');
    } finally {
      setLoadingDay(false);
    }
  }, [companyId]);

  const handleFile = async (file: File) => {
    try {
      setImporting(true);
      setError(null);
      setNotice(null);
      setFileName(file.name);
      const result = await financialReportsService.importCieloLedger(file, companyId);
      setDiff(result);
      setExpandedDiff(new Set());
      await loadCoverage();
      if (selectedDate) await openDay(selectedDate);
    } catch (err) {
      setDiff(null);
      setError(err instanceof Error ? err.message : 'Erro ao importar a planilha');
    } finally {
      setImporting(false);
    }
  };

  const confirmDecision = async () => {
    if (!pendingDecision || deciding) return;
    try {
      setDeciding(true);
      setError(null);
      const result = await financialReportsService.decideCieloDay({
        company_id: companyId,
        id_accounts_main: accountId,
        decisions: [{ row_id: pendingDecision.rowId, decision: pendingDecision.decision }],
      });
      const parts: string[] = [];
      if (result.settled_receivables > 0) parts.push(`${result.settled_receivables} parcela(s) baixada(s)`);
      if (result.transactions_created > 0) parts.push(`${result.transactions_created} lançamento(s) criado(s) (${formatCurrency(result.net_posted)})`);
      if (result.reconciled_lines > 0) parts.push('crédito do extrato reaproveitado');
      if (result.not_received_lines > 0) parts.push(`${result.not_received_lines} grupo(s) marcado(s) como não recebido(s)`);
      setNotice(`Decisão aplicada: ${parts.join(', ') || 'nada a fazer'}.`);
      setPendingDecision(null);
      await loadCoverage();
      if (selectedDate) await openDay(selectedDate);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao aplicar a decisão');
    } finally {
      setDeciding(false);
    }
  };

  const toggleDiffDate = (date: string) => {
    setExpandedDiff((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  // Grade do calendário: domingo na primeira coluna, células vazias antes do
  // dia 1 e depois do último dia.
  const calendarCells = useMemo(() => {
    const firstWeekday = new Date(cursor.year, cursor.month - 1, 1).getDay();
    const daysInMonth = new Date(cursor.year, cursor.month, 0).getDate();
    const cells: (string | null)[] = [];
    for (let i = 0; i < firstWeekday; i++) cells.push(null);
    for (let day = 1; day <= daysInMonth; day++) cells.push(isoOf(cursor.year, cursor.month, day));
    return cells;
  }, [cursor]);

  const todayISO = isoOf(today.getFullYear(), today.getMonth() + 1, today.getDate());
  const monthDays = (coverage?.days ?? []).slice().sort((a, b) => b.date.localeCompare(a.date));

  const renderGroupsTable = (groups: SettlementDayGroupView[], withActions: boolean, futureDate = false, keyPrefix = '') => (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-slate-500 uppercase tracking-wider border-b border-slate-200">
          <th className="text-left font-medium py-1.5">Situação</th>
          <th className="text-left font-medium py-1.5">Bandeira</th>
          <th className="text-left font-medium py-1.5">Tipo</th>
          <th className="text-right font-medium py-1.5">Arquivo</th>
          <th className="text-right font-medium py-1.5">Previsto</th>
          <th className="text-right font-medium py-1.5">Diferença</th>
          {withActions && <th className="text-right font-medium py-1.5">Ações</th>}
        </tr>
      </thead>
      <tbody>
        {groups.map((group, idx) => {
          const open = !group.applied && group.row_id != null;
          const entryKey = `${keyPrefix}-${group.row_id ?? `${group.brand}-${group.kind}`}-${idx}`;
          const hasEntries = (group.entries?.length ?? 0) > 0;
          const entriesOpen = hasEntries && expandedEntries.has(entryKey);
          return (
            <React.Fragment key={entryKey}>
            <tr className="border-b border-slate-100 last:border-0 align-top">
              <td className="py-1.5 pr-2">
                {group.applied ? (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium bg-emerald-50 border-emerald-200 text-emerald-700">
                    {group.applied_via === 'extrato' ? <Landmark size={11} /> : <CheckCircle2 size={11} />}
                    {group.applied_via === 'extrato'
                      ? 'Baixada pelo extrato'
                      : group.applied_via === 'automatica'
                        ? 'Baixa automática'
                        : 'Conferida'}
                  </span>
                ) : futureDate || group.scheduled ? (
                  /* Agendamento (do arquivo): ainda não é dinheiro pago — a
                     baixa libera no dia, com o crédito confirmado na conta. */
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium bg-sky-50 border-sky-200 text-sky-700">
                    <CalendarDays size={11} />
                    Agendado
                  </span>
                ) : (
                  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[11px] font-medium ${group.category === 'divergente' ? 'bg-red-50 border-red-200 text-red-700' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
                    <AlertTriangle size={11} />
                    {/* "Confere" em aberto é só a baixa que ainda não rodou — não é problema. */}
                    {group.category === 'confere'
                      ? 'Confere · aguardando baixa'
                      : CATEGORY_LABEL[group.category ?? ''] ?? group.category}
                  </span>
                )}
                {group.reason && <p className="text-[11px] text-slate-500 mt-1 max-w-56">{group.reason}</p>}
                {/* A decomposição do Detalhado: é o que autoriza "baixar" numa divergente. */}
                {!group.applied && group.category === 'divergente' && group.matched_installments != null && group.expected_installments > 0 && (
                  group.matched_installments === group.expected_installments ? (
                    <p className="text-[11px] text-emerald-700 mt-1 max-w-56">
                      As {group.expected_installments} parcela(s) prevista(s) estão pagas
                      ({formatCurrency(group.matched_net ?? 0)}); {formatCurrency(group.unidentified_net ?? 0)} são vendas fora da agenda.
                    </p>
                  ) : (
                    <p className="text-[11px] text-amber-700 mt-1 max-w-56">
                      Só {group.matched_installments} de {group.expected_installments} parcela(s)
                      prevista(s) aparecem pagas no arquivo.
                    </p>
                  )
                )}
                {group.applied && group.applied_on && (
                  <p className="text-[11px] text-slate-400 mt-1">em {formatDate(group.applied_on.slice(0, 10))}</p>
                )}
                {/* Ajustes de data desta importação: a parcela prevista foi
                    movida para a data real do pagamento (casada por TID). */}
                {(group.reallocations?.length ?? 0) > 0 && (
                  <p className="text-[11px] text-sky-700 mt-1 max-w-56">
                    {group.reallocations!.map((m) => (
                      <span key={m.receivable_id} className="block">
                        parcela {m.installment_number}/{m.installments} ajustada de{' '}
                        {formatDate(m.from_date)} → {formatDate(m.to_date)} (TID)
                      </span>
                    ))}
                  </p>
                )}
              </td>
              <td className="py-1.5">{group.brand_label || group.brand}</td>
              <td className="py-1.5 text-slate-600">{group.kind || '—'}</td>
              <td className="py-1.5 text-right whitespace-nowrap">
                {formatCurrency(group.file_net)}
                {hasEntries ? (
                  <button
                    className="flex items-center gap-0.5 ml-auto text-[11px] text-blue-700 hover:underline"
                    title="Ver as transações do arquivo: venda, parcela, pedido e status"
                    onClick={() => setExpandedEntries((prev) => {
                      const next = new Set(prev);
                      if (next.has(entryKey)) next.delete(entryKey); else next.add(entryKey);
                      return next;
                    })}
                  >
                    {group.file_transactions} transação(ões)
                    {entriesOpen ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                  </button>
                ) : (
                  <span className="block text-[11px] text-slate-400">{group.file_transactions} transação(ões)</span>
                )}
              </td>
              <td className="py-1.5 text-right whitespace-nowrap">
                {formatCurrency(group.expected_net)}
                <span className="block text-[11px] text-slate-400">{group.expected_installments} parcela(s)</span>
              </td>
              <td
                className={`py-1.5 text-right whitespace-nowrap font-medium ${group.diff_net === 0 || group.within_tolerance ? 'text-slate-400' : group.diff_net > 0 ? 'text-emerald-700' : 'text-red-600'}`}
                title={group.diff_net !== 0 && group.within_tolerance ? 'Arredondamento da Cielo, dentro da folga do grupo — não é divergência' : undefined}
              >
                {group.diff_net > 0 ? '+' : ''}{formatCurrency(group.diff_net)}
              </td>
              {withActions && (
                <td className="py-1.5 text-right whitespace-nowrap">
                  {open && futureDate && (
                    <span className="text-[11px] text-slate-400" title="Agendamento: a baixa libera no dia do pagamento, com o crédito na conta">
                      libera no dia
                    </span>
                  )}
                  {open && !futureDate && (
                    <span className="inline-flex gap-2">
                      <button
                        className="text-emerald-700 hover:underline disabled:opacity-50"
                        disabled={deciding}
                        title="Baixa as parcelas previstas e lança (ou reusa) o crédito do arquivo"
                        onClick={() => setPendingDecision({
                          rowId: group.row_id!,
                          decision: 'baixar',
                          label: `${group.brand_label || group.brand} · ${group.kind}`,
                          amountCents: group.file_net,
                        })}
                      >
                        Baixar
                      </button>
                      <button
                        className="text-red-600 hover:underline disabled:opacity-50"
                        disabled={deciding}
                        title="Marca as parcelas previstas como não recebidas"
                        onClick={() => setPendingDecision({
                          rowId: group.row_id!,
                          decision: 'nao_recebido',
                          label: `${group.brand_label || group.brand} · ${group.kind}`,
                          amountCents: group.file_net,
                        })}
                      >
                        Não recebido
                      </button>
                    </span>
                  )}
                </td>
              )}
            </tr>
            {entriesOpen && (
              /* As transações do Detalhado: venda a venda, com o vínculo do
                 pedido/proposta — é o que transforma o agregado em decisão. */
              <tr className="bg-slate-50/70">
                <td colSpan={withActions ? 7 : 6} className="py-2 pl-4 pr-2">
                  <div className="overflow-x-auto">
                    <table className="w-full text-[11px]">
                      <thead>
                        <tr className="text-slate-400 uppercase tracking-wider border-b border-slate-200">
                          <th className="text-left font-medium py-1 pr-2">Venda</th>
                          <th className="text-left font-medium py-1 pr-2">Parcela</th>
                          <th className="text-left font-medium py-1 pr-2">Pedido / Proposta</th>
                          <th className="text-left font-medium py-1 pr-2">Vínculo</th>
                          <th className="text-right font-medium py-1 pl-2">Bruto</th>
                          <th className="text-right font-medium py-1 pl-2">Taxa</th>
                          <th className="text-right font-medium py-1 pl-2">Líquido</th>
                          <th className="text-left font-medium py-1 pl-3">Status</th>
                          <th className="text-left font-medium py-1 pl-3">TID / NSU</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.entries!.map((entry) => (
                          <tr key={entry.id} className="border-b border-slate-100 last:border-0">
                            <td className="py-1 pr-2 whitespace-nowrap">{entry.sale_date ? formatDate(entry.sale_date) : '—'}</td>
                            <td className="py-1 pr-2 whitespace-nowrap">
                              {entry.installments > 1 ? `${entry.installment_number}/${entry.installments}` : 'à vista'}
                            </td>
                            <td className="py-1 pr-2 whitespace-nowrap">
                              {entry.order_id ? (
                                <a
                                  href={`/sales-orders/${entry.order_id}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="text-blue-700 hover:underline"
                                  title="Abrir o pedido em nova aba"
                                >
                                  Pedido {entry.order_id}
                                </a>
                              ) : entry.comercial_proposal_id ? (
                                <span>Proposta {entry.comercial_proposal_id}</span>
                              ) : (
                                <span className="text-slate-400">—</span>
                              )}
                            </td>
                            <td className="py-1 pr-2">
                              {entry.card_receivable_id ? (
                                <span
                                  className="inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium bg-emerald-50 border-emerald-200 text-emerald-700"
                                  title={`Parcela prevista na agenda, casada ${MATCH_SOURCE_LABEL[entry.match_source] ?? ''}`}
                                >
                                  Prevista
                                </span>
                              ) : (
                                <span
                                  className="inline-flex px-1.5 py-0.5 rounded border text-[10px] font-medium bg-amber-50 border-amber-200 text-amber-700"
                                  title={entry.match_source === 'nao_identificado'
                                    ? 'Venda que o sistema não conhece'
                                    : `Venda conhecida (casada ${MATCH_SOURCE_LABEL[entry.match_source] ?? ''}), mas sem parcela prevista para este dia — agenda parcial da migração ou pagamento deslocado de data`}
                                >
                                  Fora da agenda
                                </span>
                              )}
                            </td>
                            <td className="py-1 pl-2 text-right whitespace-nowrap">{formatCurrency(entry.gross_amount)}</td>
                            <td className="py-1 pl-2 text-right whitespace-nowrap text-slate-500">
                              {formatCurrency(entry.fee_amount)}
                              {entry.mdr_percent != null && entry.mdr_percent > 0 && (
                                <span className="block text-[10px] text-slate-400">{entry.mdr_percent.toFixed(2).replace('.', ',')}%</span>
                              )}
                            </td>
                            <td className="py-1 pl-2 text-right whitespace-nowrap font-medium">{formatCurrency(entry.net_amount)}</td>
                            <td className="py-1 pl-3 whitespace-nowrap">
                              <span className={entry.payment_status && entry.payment_status.trim().toLowerCase() === 'pago' ? 'text-emerald-700' : 'text-sky-700'}>
                                {entry.payment_status || '—'}
                              </span>
                            </td>
                            <td className="py-1 pl-3 text-slate-400 font-mono text-[10px] whitespace-nowrap">
                              {entry.tid || '—'}
                              {entry.nsu && <span className="block">NSU {entry.nsu}</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </td>
              </tr>
            )}
            </React.Fragment>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className="space-y-4">
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent><p className="text-red-600 text-sm">{error}</p></CardContent>
        </Card>
      )}
      {notice && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent><p className="text-emerald-700 text-sm">{notice}</p></CardContent>
        </Card>
      )}

      {/* Importação + banner de cobertura (o insumo e até onde ele já chegou). */}
      <Card>
        <CardContent>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="max-w-2xl">
              <p className="text-sm font-medium text-slate-800">Importar a planilha da Cielo</p>
              <p className="text-sm text-slate-600 mt-0.5">
                No portal da Cielo, exporte <strong>Vendas e Recebíveis → aba Detalhado</strong> e envie
                aqui (o Resumo não é aceito: não traz o TID das vendas). A importação carrega por{' '}
                <strong>data</strong>: reimportar o mesmo período não duplica nada — no máximo completa o
                dia que ainda estava parcial.
              </p>
              {/* O portal da Cielo identifica o estabelecimento só pelo EC:
                  é este o número a escolher antes de exportar. */}
              {coverage && (
                (coverage.establishments?.length ?? 0) > 0 ? (
                  <p className="text-sm text-slate-700 mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span>
                      Estabelecimento (EC) Cielo de {companyName}:{' '}
                      <strong className="font-mono">{coverage.establishments!.join(', ')}</strong>
                    </span>
                    <button
                      type="button"
                      className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:underline"
                      onClick={() => void copyEstablishment(coverage.establishments!.join(', '))}
                    >
                      <Copy size={12} /> {copied ? 'copiado' : 'copiar'}
                    </button>
                    {coverage.company_document && (
                      <span className="text-slate-500">· CNPJ {formatCnpj(coverage.company_document)}</span>
                    )}
                  </p>
                ) : (
                  <p className="text-sm text-amber-700 mt-2">
                    <AlertTriangle size={14} className="inline mr-1" />
                    {companyName} não tem EC Cielo cadastrado (Financeiro → Adquirentes): a importação não
                    consegue conferir se o arquivo é do estabelecimento certo.
                  </p>
                )
              )}
              {(coverage?.stale_detail?.length ?? 0) > 0 && (
                <div className="mt-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <p className="font-medium">
                    <AlertTriangle size={14} className="inline mr-1" />
                    {staleDetailMessage(coverage!.stale_detail!)}
                  </p>
                  <p className="text-xs mt-1">
                    Exporte o <strong>Detalhado</strong> de <strong>{formatDate(coverage!.stale_detail_from ?? '')}</strong> até
                    hoje e importe aqui: as vendas que faltam ganham o TID e a baixa automática fecha esses dias na manhã
                    seguinte (ou use Baixar no dia, se o crédito já está no extrato).
                  </p>
                </div>
              )}
              {coverage?.last_date ? (
                <p className="text-sm text-slate-700 mt-2">
                  <CalendarDays size={14} className="inline mr-1 text-emerald-600" />
                  Movimentação carregada de <strong>{formatDate(coverage.first_date ?? '')}</strong> até{' '}
                  <strong>{formatDate(coverage.last_date)}</strong>
                  {coverage.suggested_from ? (
                    <> · gere o próximo arquivo de <strong>{formatDate(coverage.suggested_from)}</strong> até hoje</>
                  ) : (
                    /* Cauda agendada carregada: nada falta hoje — mas o agendamento
                       envelhece (vendas novas não estão nele), daí o lembrete. */
                    <> · inclui agendamentos futuros; reimporte de tempos em tempos para
                    atualizar a agenda e capturar vendas novas</>
                  )}
                </p>
              ) : (
                !loadingCoverage && (
                  <p className="text-sm text-slate-500 mt-2">Nenhuma movimentação carregada ainda para {companyName}.</p>
                )
              )}
              {coverage?.last_import && (
                <p className="text-xs text-slate-500 mt-1">
                  Última importação: {coverage.last_import.file_name || 'arquivo'} ({formatDate(coverage.last_import.period_from)}–{formatDate(coverage.last_import.period_to)}) em{' '}
                  {formatDate(coverage.last_import.created_on.slice(0, 10))} {coverage.last_import.created_on.slice(11)} por {coverage.last_import.created_by}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {fileName && <span className="text-xs text-slate-500 max-w-[220px] truncate">{fileName}</span>}
              <input
                ref={inputRef}
                type="file"
                accept=".xlsx,.csv"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) void handleFile(file);
                  e.target.value = '';
                }}
              />
              <Button size="sm" loading={importing} onClick={() => inputRef.current?.click()} icon={<Upload size={14} />}>
                Importar arquivo
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Diff da importação: as datas sem ação vêm colapsadas, expansíveis. */}
      {diff && (
        <Card>
          <CardContent>
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <span className="font-medium text-slate-800">Resultado da importação ({formatDate(diff.period_from)}–{formatDate(diff.period_to)}):</span>
              {diff.dates_new > 0 && <span className="px-2 py-0.5 rounded-full border text-xs font-medium bg-sky-50 border-sky-200 text-sky-700">{diff.dates_new} data(s) nova(s)</span>}
              {diff.dates_unchanged > 0 && <span className="px-2 py-0.5 rounded-full border text-xs font-medium bg-slate-50 border-slate-200 text-slate-600">{diff.dates_unchanged} sem mudança</span>}
              {diff.dates_updated > 0 && <span className="px-2 py-0.5 rounded-full border text-xs font-medium bg-amber-50 border-amber-200 text-amber-700">{diff.dates_updated} com registros novos</span>}
              {diff.dates_applied > 0 && <span className="px-2 py-0.5 rounded-full border text-xs font-medium bg-emerald-50 border-emerald-200 text-emerald-700">{diff.dates_applied} já aplicada(s)</span>}
              {diff.pending_days > 0 && <span className="px-2 py-0.5 rounded-full border text-xs font-medium bg-red-50 border-red-200 text-red-700">{diff.pending_days} pedem decisão</span>}
            </div>
            {diff.establishment_check === 'ok' && (
              <p className="text-xs text-emerald-700 mt-2">
                <CheckCircle2 size={12} className="inline mr-1" />
                EC confere: {(diff.file_establishments ?? []).join(', ')}
              </p>
            )}
            {diff.establishment_check === 'nao_informado' && (
              <p className="text-xs text-slate-500 mt-2">
                O arquivo não informa o EC das vendas — confira no portal da Cielo se exportou o estabelecimento certo.
              </p>
            )}
            {(diff.stale_detail?.length ?? 0) > 0 && (
              <p className="text-xs text-red-700 mt-2">
                <AlertTriangle size={12} className="inline mr-1" />
                {staleDetailMessage(diff.stale_detail!)} Este arquivo não cobriu essas datas — exporte o Detalhado a partir
                de {formatDate(diff.stale_detail![0].date)}.
              </p>
            )}
            {(diff.acquirer_reversals?.length ?? 0) > 0 && (
              <div className="text-xs text-orange-800 mt-2">
                <AlertTriangle size={12} className="inline mr-1" />
                A Cielo desfez {diff.acquirer_reversals!.length} venda(s) (chargeback ou cancelamento) — as parcelas
                previstas saíram da agenda:
                <ul className="ml-5 mt-1 list-disc">
                  {diff.acquirer_reversals!.map((rev) => (
                    <li key={rev.credit_card_payment_id}>
                      débito de {formatCurrency(rev.debit_net)} em {formatDate(rev.debit_date)} (TID {rev.tid}) ·{' '}
                      {rev.receivable_ids.length} parcela(s) estornada(s), {formatCurrency(rev.reversed_net)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {diff.gap_from && (
              <p className="text-xs text-amber-700 mt-2">
                <AlertTriangle size={12} className="inline mr-1" />
                Há um buraco na cobertura: nenhum arquivo trouxe {formatDate(diff.gap_from)} a {formatDate(diff.gap_to ?? diff.gap_from)}. Gere um arquivo cobrindo essas datas.
              </p>
            )}
            {diff.partial_today && (
              <p className="text-xs text-slate-500 mt-1">
                O arquivo termina hoje — o dia ainda pode ganhar lançamentos e será completado na próxima importação.
              </p>
            )}

            <div className="mt-3 divide-y divide-slate-100">
              {diff.days.map((day: SettlementImportDayDiff) => {
                const meta = OUTCOME_META[day.outcome] ?? OUTCOME_META.sem_mudanca;
                const expanded = day.needs_action || expandedDiff.has(day.date);
                return (
                  <div key={day.date} className="py-2">
                    <button
                      className="w-full flex items-center justify-between gap-2 text-left"
                      onClick={() => { if (!day.needs_action) toggleDiffDate(day.date); }}
                    >
                      <span className="flex items-center gap-2 text-sm">
                        <span className="font-medium text-slate-800 whitespace-nowrap">{formatDate(day.date)}</span>
                        <span className={`px-2 py-0.5 rounded-full border text-xs font-medium ${meta.className}`}>{meta.label}</span>
                        {day.outcome === 'registros_novos' && (
                          <span className="text-xs text-amber-700">+{day.new_transactions} transação(ões) ({formatCurrency(day.new_net ?? 0)})</span>
                        )}
                        {day.diverging_groups > 0 && (
                          <span className="text-xs text-red-600">{day.diverging_groups} divergência(s)</span>
                        )}
                      </span>
                      <span className="flex items-center gap-3 text-sm">
                        <span className="text-slate-600 whitespace-nowrap">{formatCurrency(day.file_net)}</span>
                        {day.needs_action ? (
                          <span
                            role="button"
                            tabIndex={0}
                            className="text-xs text-emerald-700 hover:underline whitespace-nowrap"
                            onClick={(e) => { e.stopPropagation(); void openDay(day.date); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); void openDay(day.date); } }}
                          >
                            decidir no dia →
                          </span>
                        ) : expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                      </span>
                    </button>
                    {expanded && (
                      <div className="mt-2 overflow-x-auto">
                        {renderGroupsTable(day.groups, false, day.date > todayISO, `diff-${day.date}`)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Calendário + lista do mês. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <button
                className="p-1.5 text-slate-500 hover:text-slate-800 rounded"
                onClick={() => setCursor((c) => (c.month === 1 ? { year: c.year - 1, month: 12 } : { ...c, month: c.month - 1 }))}
              >
                <ChevronLeft size={16} />
              </button>
              <p className="text-sm font-medium text-slate-800">
                {MONTH_LABELS[cursor.month - 1]} {cursor.year}
                {loadingCoverage && <Loader2 size={13} className="inline ml-2 animate-spin text-slate-400" />}
              </p>
              <button
                className="p-1.5 text-slate-500 hover:text-slate-800 rounded"
                onClick={() => setCursor((c) => (c.month === 12 ? { year: c.year + 1, month: 1 } : { ...c, month: c.month + 1 }))}
              >
                <ChevronRight size={16} />
              </button>
            </div>
            {/* Células baixas de propósito: o calendário é um mapa de estado,
                não o palco da tela — a leitura fina acontece na lista ao lado. */}
            <div className="grid grid-cols-7 gap-0.5 text-center">
              {WEEKDAY_LABELS.map((label, idx) => (
                <span key={`${label}-${idx}`} className="text-[10px] font-medium text-slate-400 py-0.5">{label}</span>
              ))}
              {calendarCells.map((date, idx) => {
                if (!date) return <span key={`empty-${idx}`} />;
                const day = daysByDate.get(date);
                const meta = day ? STATUS_META[day.status] : null;
                return (
                  <button
                    key={date}
                    className={[
                      'h-6 rounded text-[11px] font-medium flex items-center justify-center transition-colors',
                      meta ? meta.cell : 'text-slate-400 hover:bg-slate-50',
                      date === todayISO ? 'ring-1 ring-slate-400' : '',
                      selectedDate === date ? 'ring-2 ring-emerald-500' : '',
                    ].join(' ')}
                    title={day ? `${STATUS_META[day.status].label} · ${formatCurrency(day.file_net)}` : 'Sem movimentação carregada'}
                    onClick={() => { if (day) void openDay(date); }}
                  >
                    {Number(date.slice(8, 10))}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-2 text-[11px] text-slate-500">
              <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-100 border border-emerald-300 mr-1" />Conferida</span>
              <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-100 border border-amber-300 mr-1" />Pendente</span>
              <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-100 border border-red-300 mr-1" />Divergente</span>
              <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-sky-100 border border-sky-300 mr-1" />Agendada</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-slate-500 uppercase tracking-wider border-b border-slate-200">
                  <th className="text-left font-medium py-2 px-4">Data</th>
                  <th className="text-left font-medium py-2">Situação</th>
                  <th className="text-right font-medium py-2 px-4">Valor pago</th>
                </tr>
              </thead>
              <tbody>
                {monthDays.length === 0 ? (
                  <tr>
                    <td colSpan={3} className="text-center text-slate-500 py-6 text-sm">
                      Sem movimentação carregada neste mês.
                    </td>
                  </tr>
                ) : (
                  monthDays.map((day) => {
                    const meta = STATUS_META[day.status];
                    return (
                      <tr
                        key={day.date}
                        className={`border-b border-slate-100 last:border-0 cursor-pointer hover:bg-slate-50 ${selectedDate === day.date ? 'bg-emerald-50/50' : ''}`}
                        onClick={() => void openDay(day.date)}
                      >
                        <td className="py-2 px-4 whitespace-nowrap">{formatDate(day.date)}</td>
                        <td className="py-2">
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-xs font-medium ${meta.badge}`}>{meta.label}</span>
                          {day.pending_groups > 0 && (
                            <span className="ml-2 text-xs text-slate-500">{day.pending_groups} grupo(s) em aberto</span>
                          )}
                        </td>
                        <td className="py-2 px-4 text-right whitespace-nowrap">{formatCurrency(day.file_net)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Detalhe do dia selecionado, com as ações. */}
      {selectedDate && (
        <div ref={dayDetailRef}>
        <Card>
          <CardContent>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-slate-800">
                {formatDate(selectedDate)}
                {dayDetail && (
                  <span className={`ml-2 px-2 py-0.5 rounded-full border text-xs font-medium ${STATUS_META[dayDetail.status].badge}`}>
                    {STATUS_META[dayDetail.status].label}
                  </span>
                )}
                {loadingDay && <Loader2 size={13} className="inline ml-2 animate-spin text-slate-400" />}
              </p>
              {dayDetail && <span className="text-sm text-slate-600">Total pago: <strong>{formatCurrency(dayDetail.file_net)}</strong></span>}
            </div>

            {pendingDecision && (
              <div className="mb-3 p-3 rounded-lg border border-amber-200 bg-amber-50 text-sm flex flex-wrap items-center justify-between gap-2">
                <span className="text-amber-800">
                  {pendingDecision.decision === 'baixar' ? (
                    <>Baixar <strong>{pendingDecision.label}</strong> ({formatCurrency(pendingDecision.amountCents)}) na conta <strong>{accountLabel}</strong>?</>
                  ) : (
                    <>Marcar <strong>{pendingDecision.label}</strong> como não recebido?</>
                  )}
                </span>
                <span className="flex gap-2">
                  <Button size="sm" loading={deciding} onClick={confirmDecision}>Confirmar</Button>
                  <button className="text-xs text-slate-500 hover:underline" onClick={() => setPendingDecision(null)}>Cancelar</button>
                </span>
              </div>
            )}

            {dayDetail && dayDetail.groups.length === 0 && !loadingDay && (
              <p className="text-sm text-slate-500">Sem movimentação carregada nesta data.</p>
            )}
            {dayDetail && dayDetail.groups.length > 0 && (
              <div className="overflow-x-auto">
                {renderGroupsTable(dayDetail.groups, true, selectedDate > todayISO, `day-${dayDetail.date}`)}
              </div>
            )}
            <p className="text-[11px] text-slate-400 mt-2 inline-flex items-center gap-1">
              <History size={11} />
              Grupos já aplicados são imutáveis; para desfazer, exclua o lançamento na Conta Corrente.
            </p>
          </CardContent>
        </Card>
        </div>
      )}
    </div>
  );
};
